// outreach-automation.js - Tier 3 Solution Page
export const content = {
  heroSubtitle: "Automated outreach that feels personal. The right message to the right client at the right time—without the manual work of making it happen.",

  problemSection: {
    title: "The Outreach Problem",
    html: `
      <p>You know you should be reaching out to clients regularly. Quarterly check-ins. Birthday calls. Pre-AEP touchpoints. Service follow-ups. The list goes on.</p>

      <p>But here's the reality: most agents don't do it consistently. Not because they don't care—because there's always something more urgent. A new lead to chase. A problem to solve. A sale to close. The retention touchpoints that don't scream for attention get pushed to "later." And later becomes never.</p>

      <p>The result? Clients who haven't heard from you in months. Relationships that fade. Competitors who step into the silence you've left. Churn that could have been prevented.</p>

      <p>Outreach automation solves this. Not by replacing the personal touch, but by making sure the personal touch actually happens—systematically, reliably, at scale.</p>
    `
  },

  features: [
    {
      icon: "clock",
      title: "Scheduled Touchpoints",
      description: "Set up quarterly check-ins, birthday outreach, policy anniversaries—all scheduled automatically. Never miss a touchpoint again."
    },
    {
      icon: "zap",
      title: "Triggered Workflows",
      description: "Automatic outreach when events happen: post-enrollment check-in, service issue follow-up, plan change notification. The right response at the right time."
    },
    {
      icon: "users",
      title: "Personalized at Scale",
      description: "Templates that insert client details automatically. Mass outreach that doesn't feel mass. Personal communication without personal time investment."
    },
    {
      icon: "bell",
      title: "Smart Reminders",
      description: "When outreach requires your personal touch (calls, complex situations), you get reminded. System handles what it can, prompts you for the rest."
    },
    {
      icon: "chart",
      title: "Engagement Tracking",
      description: "See who opened emails, who responded, who needs follow-up. Know which outreach is working and who's going silent."
    },
    {
      icon: "shield",
      title: "Compliance Built-In",
      description: "Templates designed with CMS marketing rules in mind. Communication that keeps you compliant while keeping clients engaged."
    }
  ],

  featuresTitle: "How Outreach Automation Works",

  mainContent: `
    <h2>The Two Types of Outreach</h2>

    <h3>Scheduled Outreach</h3>
    <p>These are touchpoints that should happen on a regular cadence:</p>
    <ul>
      <li><strong>Quarterly check-ins:</strong> "How's everything going?"</li>
      <li><strong>Birthday acknowledgments:</strong> Personal recognition that builds loyalty</li>
      <li><strong>Policy anniversaries:</strong> "It's been a year since we got you set up"</li>
      <li><strong>Pre-AEP preparation:</strong> "Enrollment season is coming, let's review"</li>
      <li><strong>Post-AEP follow-up:</strong> "How's your new coverage working?"</li>
    </ul>

    <p>With automation, you set the schedule once. The system handles the timing, the reminders, the execution. Clients hear from you consistently without you having to remember to do it.</p>

    <h3>Triggered Outreach</h3>
    <p>These are touchpoints that should happen when specific events occur:</p>
    <ul>
      <li><strong>Post-enrollment:</strong> Automatic welcome and onboarding after new enrollment</li>
      <li><strong>Service issue resolution:</strong> Follow-up after a problem is fixed</li>
      <li><strong>Plan changes detected:</strong> Alert when their plan's benefits, network, or formulary changes</li>
      <li><strong>Silence detected:</strong> Outreach when you haven't connected in X days</li>
      <li><strong>Risk score increase:</strong> Prompt to reach out when churn risk rises</li>
    </ul>

    <p>Triggers ensure you respond to events in real-time, not when you happen to notice them.</p>

    <h2>Personalization That Scales</h2>

    <p>The fear with automation is losing the personal touch. But good automation enhances personalization, not replaces it.</p>

    <h3>Dynamic Fields</h3>
    <p>Templates automatically insert:</p>
    <ul>
      <li>Client name</li>
      <li>Plan details</li>
      <li>Enrollment date</li>
      <li>Last interaction</li>
      <li>Specific details relevant to their situation</li>
    </ul>

    <h3>Conditional Content</h3>
    <p>Different messaging for different situations:</p>
    <ul>
      <li>New enrollee gets onboarding content</li>
      <li>Long-term client gets anniversary acknowledgment</li>
      <li>Client with recent issue gets follow-up specific to that issue</li>
    </ul>

    <h3>Channel Matching</h3>
    <p>Reach clients how they prefer:</p>
    <ul>
      <li>Email for detailed information</li>
      <li>Text for quick check-ins</li>
      <li>Phone call reminders for conversations that need voice</li>
    </ul>

    <h2>What Can Be Automated vs. What Needs You</h2>

    <p>Not everything should be automated. The system handles:</p>
    <ul>
      <li>Scheduling and reminders</li>
      <li>Routine acknowledgments (birthdays, anniversaries)</li>
      <li>Information-sharing communications</li>
      <li>Simple check-in messages</li>
      <li>Follow-up prompts</li>
    </ul>

    <p>You handle:</p>
    <ul>
      <li>Complex conversations (plan reviews, problem-solving)</li>
      <li>Situations requiring judgment</li>
      <li>High-risk client interventions</li>
      <li>Relationship-critical moments</li>
    </ul>

    <p>The goal isn't to remove you from client relationships—it's to ensure the relationships get the attention they need, with your personal involvement where it matters most.</p>

    <h2>The Workflow Builder</h2>

    <p>Pro IRP lets you build custom outreach workflows:</p>

    <h3>Example: New Enrollee Onboarding</h3>
    <ol>
      <li>Day 1: Welcome email with coverage overview</li>
      <li>Day 3: Text asking if they have questions</li>
      <li>Day 14: Reminder to call for first check-in</li>
      <li>Day 30: Email about how to use benefits</li>
      <li>Day 60: Text checking in on satisfaction</li>
      <li>Day 90: Call reminder for quarterly review</li>
    </ol>

    <h3>Example: Annual Retention Cycle</h3>
    <ol>
      <li>Birthday: Automated message + call reminder</li>
      <li>Q2: Mid-year check-in email</li>
      <li>August: Pre-AEP preparation outreach</li>
      <li>September: ANOC follow-up reminder</li>
      <li>October: Annual review scheduling</li>
      <li>January: Post-enrollment check-in</li>
    </ol>

    <h2>Measuring Outreach Effectiveness</h2>

    <p>Automation includes tracking so you know what's working:</p>
    <ul>
      <li><strong>Open rates:</strong> Are clients reading your emails?</li>
      <li><strong>Response rates:</strong> Are they engaging?</li>
      <li><strong>Retention correlation:</strong> Do clients who receive outreach retain better?</li>
      <li><strong>Channel performance:</strong> Which channels work best for different messages?</li>
    </ul>

    <p>This data lets you optimize over time. Double down on what works, adjust what doesn't.</p>

    <h2>Getting Started</h2>

    <p>Implementation is straightforward:</p>
    <ol>
      <li>Import your client list</li>
      <li>Customize pre-built workflow templates</li>
      <li>Set your outreach preferences and schedule</li>
      <li>Let the system run</li>
    </ol>

    <p>Most agents are up and running within a day. The templates handle common scenarios—you just personalize to your voice and style.</p>

    <h2>The ROI of Consistent Outreach</h2>

    <p>Let's be concrete:</p>
    <ul>
      <li>Average agent retention: 80%</li>
      <li>Agents with systematic outreach: 88-92%</li>
      <li>For 200 clients at $300/year: 8% improvement = $4,800/year in protected revenue</li>
    </ul>

    <p>The time you'd spend on manual outreach (or more likely, not doing it) is replaced by automated systems that work even when you're busy with other things. Retention happens in the background while you focus on growth.</p>
  `,

  faqs: [
    {
      question: "Won't automated outreach feel impersonal to clients?",
      answer: "Good automation enhances personalization, not replaces it. Templates include client-specific details, acknowledge their situation, and feel personal. The key is automation that handles timing and logistics while keeping the message human."
    },
    {
      question: "What types of outreach work best when automated?",
      answer: "Best for automation: scheduling/reminders, birthday acknowledgments, routine check-ins, information sharing, simple follow-ups. Keep personal: complex conversations, problem-solving, high-stakes interventions, situations requiring judgment."
    },
    {
      question: "How do I know if my automated outreach is working?",
      answer: "Track open rates, response rates, and most importantly, retention correlation. Are clients who receive consistent outreach staying longer? This data tells you what's working and what to adjust."
    },
    {
      question: "Is automated outreach compliant with CMS marketing rules?",
      answer: "Pro IRP templates are designed with CMS compliance in mind. That said, always ensure your outreach follows current Medicare marketing guidelines, especially for anything that could be considered plan promotion."
    },
    {
      question: "How long does it take to set up outreach automation?",
      answer: "Most agents are operational within a day using pre-built templates. You'll customize the templates to your voice and set your preferred schedules. No technical expertise required—the system handles the complexity."
    }
  ],

  sources: [
    {
      title: "Medicare Marketing Guidelines",
      url: "https://www.cms.gov/Medicare/Health-Plans/ManagedCareMarketing",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
